/**
 * Display formatters shared by every table and detail surface.
 *
 * These exist because the repo had 38 independent `new Date(...).toLocale*()`
 * call sites and no helper, so the same `createdAt` field rendered date-only in
 * one table and date+time in another. Route every value through here.
 *
 * The em dash (not an empty string) is the canonical "no value" rendering — an
 * empty cell reads as a layout bug, a dash reads as intentional.
 */

/** The canonical "no value" rendering. */
export const EM_DASH = '—';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Bare `YYYY-MM-DD` (a Postgres DATE — conference start/end dates) is a
 * calendar day, not an instant. `new Date('YYYY-MM-DD')` parses it as UTC
 * midnight, which the `toLocale*` renderers then shift into the viewer's zone
 * — a day early anywhere west of UTC. Build it as local midnight instead so it
 * renders as the day written. Anything with a time component still goes
 * through `Date`'s own parser.
 */
function parse(v: string | null | undefined): Date | null {
  if (!v) return null;
  const m = DATE_ONLY.exec(v);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const local = new Date(y, mo - 1, d);
    // `Date` rolls invalid parts over (month 13 → next year); reject those.
    const roundTrips =
      local.getFullYear() === y && local.getMonth() === mo - 1 && local.getDate() === d;
    return roundTrips ? local : null;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date only, in the viewer's locale. */
export function formatDate(v: string | null | undefined): string {
  const d = parse(v);
  return d ? d.toLocaleDateString() : EM_DASH;
}

/** Date + time, in the viewer's locale. */
export function formatDateTime(v: string | null | undefined): string {
  const d = parse(v);
  return d ? d.toLocaleString() : EM_DASH;
}

/**
 * Renders a reference to another entity. Deliberately NEVER falls back to the
 * id: a bare GUID in a table cell is noise the operator cannot act on. Screens
 * that need the id should surface it in the detail page instead.
 */
export function formatEntityRef(ref: {
  id?: string;
  name?: string | null;
}): string {
  return ref.name ? ref.name : EM_DASH;
}
